import { Footer } from "@components/Footer";
import { DoneIcon } from "@components/Icons/Done";
import { SpinnerIcon } from "@components/Icons/Spinner";
import { Link } from "@components/Link";
import { Loading } from "@components/Loading";
import { PremiumModal } from "@components/PremiumModal/PremiumModal";
import { TopNav } from "@components/TopNav";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { usePersistedState } from "src/helpers/usePersistedState";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useTeamId } from "src/helpers/useTeam";
import { create } from "zustand";

type ChallengeId = string;
export type Challlenges = Map<ChallengeId, boolean>;

export const useProgressStore = create((set) => ({
  challenges: {} as Challenges,
  setChallenges: (challenges: ChallengeId[]) => {
    set((state) => {
      const newChallenges = challenges.reduce(
        (acc, challengeId) => {
          if (!(challengeId in state.challenges)) {
            acc[challengeId] = false;
          } else {
            acc[challengeId] = state.challenges[challengeId];
          }
          return acc;
        },
        { ...state.challenges },
      );
      return { challenges: newChallenges };
    });
  },
  setChallenge: (challengeId: ChallengeId, loading: boolean) => {
    set((state) => ({
      challenges: {
        ...state.challenges,
        [challengeId]: loading,
      },
    }));
  },
}));

type ChallengeWithCompleted = Challenge & { completed: boolean };

export const Challenges: FC = () => {
  const { t } = useTranslation("challenges");

  const [appData, setAppData] = usePersistedState();
  const eventId = useEventId();
  const [event] = useEvent();
  const teamId = useTeamId();

  const [challenges] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);
  const [solutions] = useSimpleCollection<Solution>(`events/${eventId}/teams/${teamId}/solutions`);

  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const navigate = useEventNavigate();

  // @ts-ignore
  const setChallengeLoadingStates = useProgressStore((state) => state.setChallenges);
  // @ts-ignore
  const challengeLoadingStates = useProgressStore((state) => state.challenges);

  const [challengesWithSolutions, setChallengesWithSolutions] = useState<ChallengeWithCompleted[]>([]);
  useEffect(() => {
    const challengesWithSolutions = challenges?.map((challenge) => {
      const solution = solutions?.find((solution) => solution.id === challenge.id);
      return { ...challenge, completed: !!solution?.completed };
    });
    setChallengesWithSolutions(challengesWithSolutions);

    const challengeIds = challengesWithSolutions.map((challenge) => challenge.id);
    setChallengeLoadingStates(challengeIds);
  }, [challenges, solutions]);

  // Restore scroll position the first time user navigates to this page
  // Scroll position is captured in the location state when user clicks on a challenge
  useEffect(() => {
    const hasScrollPosition = !!appData?.persistedScrollPosition;
    const hasChallenges = challengesWithSolutions?.length > 0;
    if (!hasScrollPosition || !hasChallenges) return;

    window.scrollTo(0, appData.persistedScrollPosition);
    setAppData({ ...appData, persistedScrollPosition: undefined });
  }, [challengesWithSolutions]);

  useEffect(() => {
    window.onbeforeunload = () => {
      setAppData({ ...appData, persistedScrollPosition: window?.scrollY });
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  if (!challenges || !solutions) return null;

  const filterHiddenChallenges = (challenge: Challenge) => !challenge.hidden;
  const filterNonCompletableChallenges = (challenge: Challenge) =>
    challenge.type !== "song" && challenge.type !== "info"; // song and info challenges don't count as completable

  const orderedChallenges = useMemo(() => {
    if (!event || !challengesWithSolutions) return [];
    let ordered = challengesWithSolutions;
    if (event.order) {
      ordered = event.order.map((id) => challengesWithSolutions?.find((challenge) => challenge.id === id));

      // also find any that are not in the order and add them to the end
      const notOrdered = challengesWithSolutions?.filter((challenge) => !event.order.includes(challenge.id));
      ordered = [...ordered, ...notOrdered];
    }
    return ordered?.filter((challenge) => !!challenge).filter(filterHiddenChallenges);
  }, [event, challengesWithSolutions]);

  const persistScrollPosition = () => {
    setAppData({ ...appData, persistedScrollPosition: window?.scrollY });
  };

  const onChallengeClick = (challenge: Challenge & Solution) => {
    persistScrollPosition();
    navigate(`/challenges/${challenge.id}`);
  };

  const { getTranslatedValue } = useTranslatedKey();

  const isTrial = !!event?.isTrial;

  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const handleOverlayClick = () => setShowCompletionOverlay(false);
  useMemo(() => {
    if (orderedChallenges?.length === 0) return;

    const completableChallenges = (orderedChallenges ?? []).filter(filterNonCompletableChallenges);
    // .every() is vacuously true on an empty list — an event with only song/info challenges must
    // never celebrate "all completed"
    const allCompleted = completableChallenges.length > 0 && completableChallenges.every((c) => c.completed);
    if (allCompleted) setShowCompletionOverlay(true);
  }, [orderedChallenges]);

  // ── ONNINEN (event-specific, throwaway) ──────────────────────────────
  // Temporary code - onninen event - remove later
  const [showHalfCompletionOverlay, setShowHalfCompletionOverlay] = useState(false);
  const handleHalfOverlayClick = () => setShowHalfCompletionOverlay(false);
  useMemo(() => {
    if (orderedChallenges?.length === 0) return;

    const halfCompletedChallenges = (orderedChallenges ?? [])
      .filter(filterNonCompletableChallenges)
      .filter((challenge) => challenge.completed);
    console.log(halfCompletedChallenges);
    const isHalfCompleted = halfCompletedChallenges.length === 10;

    const isOnninen = eventId === "onninen";
    if (isHalfCompleted && isOnninen) setShowHalfCompletionOverlay(true);
  }, [orderedChallenges]);
  // ─────────────────────────────────────────────────────────────────────

  const isPromoEvent = event?.isPromo ?? false;

  const isUrboStogos = eventId === "urbostogos";
  const aboutTitle = isUrboStogos ? "Apačioje galite peržiūrėti ir įvykdyti atostogų nuotykius." : t("aboutTitle");

  return (
    <div className="h-screen">
      <main>
        <TopNav isAdmin={false} />
        {showPremiumModal && <PremiumModal show={showPremiumModal} onClose={() => setShowPremiumModal(false)} />}
        <Loading loading={!challenges || !solutions}>
          {showCompletionOverlay && (
            <div
              className="z-100 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
              onClick={handleOverlayClick}
            >
              <div className="mx-8 flex flex-col rounded-xl bg-base-100 p-8 text-center text-2xl font-bold">
                <div>{t("congratulationsAllCompleted")}</div>
                <div className="mt-8 text-center text-xs font-extralight text-neutral">{t("clickAnywhereToClose")}</div>
              </div>
            </div>
          )}
          {/* ── ONNINEN (event-specific, throwaway) ────────────────────────────── */}
          {showHalfCompletionOverlay && (
            <div
              className="z-100 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
              onClick={handleHalfOverlayClick}
            >
              <div className="mx-8 flex flex-col rounded-xl bg-base-100 p-8 text-center text-2xl font-bold">
                <div>{t("congratulationsHalfCompleted")}</div>
                <div className="mt-8 text-center text-xs font-extralight text-neutral">{t("clickAnywhereToClose")}</div>
              </div>
            </div>
          )}
          {/* ───────────────────────────────────────────────────────────────────── */}
          <div className="flex flex-col items-center justify-center px-8">
            <h1 className={"mt-12 mb-8 rounded-xl px-4 text-3xl font-bold"}>{t("title")}</h1>
            <p className="mb-8 text-center">{aboutTitle}</p>
          </div>
          <div className="mt-4 mb-8 flex w-full flex-col items-center">
            <div className="w-full px-6 md:px-32 xl:px-64 xl:px-96">
              {orderedChallenges.map((challenge, index) => {
                const { id, completed } = challenge;
                const title = getTranslatedValue(challenge, "title");
                const about = getTranslatedValue(challenge, "about");

                const trialDegrade = isTrial && index > 2; // blur all but the first few challenges if it's a trial event
                const isUploading = challengeLoadingStates[id];
                const handleChallengeClick = () => {
                  if (isUploading || trialDegrade) return;
                  return onChallengeClick(challenge);
                };

                const votingCustomise = challenge.type === "voting";

                // ── URBOSTOGOS (event-specific, throwaway) ──────────────
                // "PAGUNDA" challenges get a dark-green tinted card. Remove later.
                const isPagunda = isUrboStogos && title?.startsWith("PAGUNDA");

                return (
                  <div key={id} className="mb-6" role="button" onClick={handleChallengeClick}>
                    <div
                      className={`rounded-2xl ${
                        isPagunda
                          ? "bg-emerald-700 bg-opacity-60"
                          : votingCustomise
                          ? "bg-cyan-700 bg-opacity-70"
                          : "bg-base-200 bg-opacity-90"
                      }`}
                    >
                      {trialDegrade && (
                        <div className="flex justify-center">
                          <div className="z-100 absolute mt-12 text-center ">
                            <span className="p-4 text-center font-black text-black">{t("upgradeToUnlock")}</span>
                          </div>
                        </div>
                      )}
                      <div
                        className={`card w-full border border-base-300 ${
                          completed ? " bg-success bg-opacity-40" : "bg-base-200"
                        } ${trialDegrade ? "bg-opacity-40 blur" : ""} shadow-xl ${
                          isUploading ? "animate-pulse opacity-50" : ""
                        } ${
                          isPagunda
                            ? "border-emerald-700 bg-emerald-200 bg-opacity-30"
                            : votingCustomise
                            ? "border-cyan-500 bg-cyan-200 bg-opacity-30"
                            : "border-base-300"
                        }`}
                      >
                        <div className="flex justify-between">
                          <div className="w-full">
                            <div className="card-body">
                              {completed && (
                                <div className="-m-1 flex justify-center">
                                  <div className="absolute text-center text-gray-100 sm:-mt-2">
                                    <DoneIcon className="h-16 w-16" />
                                  </div>
                                </div>
                              )}

                              {isUploading && (
                                <div className="-mt-1 flex justify-center">
                                  <div className="absolute text-center text-gray-100">
                                    <SpinnerIcon className="h-16 w-16" />
                                  </div>
                                </div>
                              )}

                              <div className={`${completed || trialDegrade ? "blur-xs" : ""}`}>
                                {votingCustomise && (
                                  <h2 className="pb-1 text-left text-2xl font-bold text-cyan-800">{t("voting")}</h2>
                                )}
                                <div className="flex justify-between">
                                  {/* ── URBOSTOGOS (event-specific, throwaway) ──────────────
                                      "PAGUNDA" challenges rendered in dark green. Remove later. */}
                                  <h2
                                    className="card-title"
                                    style={
                                      isUrboStogos && title?.startsWith("PAGUNDA") ? { color: "#23725f" } : undefined
                                    }
                                  >
                                    {title}
                                  </h2>
                                </div>
                                {about && <p className="min-h-6">{about}</p>}
                              </div>
                              {/* Betting completion subtext */}
                              {challenge.type === "betting" &&
                                completed &&
                                (challenge as BettingChallenge).bettingsClosed && (
                                  <p className="mt-3 text-center text-sm font-semibold text-neutral">
                                    {t("bettingCompletedSubtext")}
                                  </p>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {!isPromoEvent && (
            <Link to="upload">
              <div className="flex justify-center">
                <div
                  className={`mt-4 w-full pb-12 text-center text-sm font-bold text-neutral text-opacity-80 underline`}
                >
                  <span>{t("uploadPageHint")}</span>
                </div>
              </div>
            </Link>
          )}
        </Loading>
      </main>
      <Footer />
    </div>
  );
};
